import dayjs from "dayjs";
import svLocale from "dayjs/locale/sv";
import { Calendar, CalendarStyles, Popover } from "frost-ui-react";
import React, { useState } from "react";
import { useNavigate } from "react-router";
import styled from "styled-components";
import TimeReport from "../../../models/TimeReport";
import { appTheme } from "../../../theme/Theme";
import { shiftTimeFromTo } from "../../../utils/date.time.utils";
import { jobRoute } from "../../../utils/route.utils";
import AppText, { LinkText } from "../../Core/AppText/AppText";
import Flex from "../../Layout/Flex";

interface Props {
	timeReports: TimeReport[];
}

const TimeReportCalendar: React.FC<Props> = ({ timeReports }) => {
	const [closePopoverTimer, setClosePopoverTimer] = useState<NodeJS.Timeout>();
	const navigate = useNavigate();

	const [timePopover, setTimePopover] = useState<{
		left: number;
		top: number;
		timeReports: TimeReport[];
	}>();

	const dates: { date: Date; type: string }[] = timeReports.map((shift) => ({
		type: "value",
		date: new Date(shift.reported.start),
	}));

	return (
		<TimeReportCalendarStyle>
			<Calendar
				locale={svLocale}
				styles={calendarStyles}
				highlights={dates}
				onHover={(e, date) => {
					const { offsetLeft, offsetTop } = e.currentTarget;

					const dayShifts = timeReports.filter((shift) => {
						return dayjs(shift.reported.start).isSame(date, "day");
					});

					if (dayShifts.length) {
						if (closePopoverTimer) {
							clearTimeout(closePopoverTimer);
							setClosePopoverTimer(undefined);
						}

						setTimePopover({
							left: offsetLeft - 35,
							top: offsetTop + 50,
							timeReports: dayShifts,
						});
					} else {
						// Note: The time logic is for adding slight delay when un hovering
						if (!closePopoverTimer) {
							const timer = setTimeout(() => {
								setTimePopover(undefined);
							}, 400);

							setClosePopoverTimer(timer);
						}
					}
				}}
			/>
			{timePopover && (
				<div className="time-popover" style={{ left: timePopover.left, top: timePopover.top }}>
					<Popover pointTo="top" styles={{ arrowSize: 12, offset: "50px", width: 290 }}>
						{timePopover.timeReports.map((shift) => (
							<Flex key={shift.id} justifyContent="space-between">
								<LinkText lineClamp={1} onClick={() => navigate(jobRoute(shift.job?.id || ""))}>
									#{shift.job?.orderNumber}, {shift.job?.customer.name}
								</LinkText>
								<AppText margin="0 0 0 15px">{shiftTimeFromTo(shift.reported)}</AppText>
							</Flex>
						))}
					</Popover>
				</div>
			)}
		</TimeReportCalendarStyle>
	);
};

export default TimeReportCalendar;

const TimeReportCalendarStyle = styled.div`
	background-color: white;
	padding: 1.5rem 1rem 0.75rem 1rem;
	border-radius: 20px;
	position: relative;

	.time-popover {
		position: absolute;
		z-index: 5;
	}
`;

const calendarStyles: CalendarStyles = {
	highlightStyles: {
		value: {
			backgroundColor: appTheme.colors.COLOR_ACTION_BLUE,
			color: appTheme.colors.COLOR_WHITE,
		},
	},
};
